import * as React from 'react';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import '../Main.scss';
import './RunAuditWorkpapersModal.scss';

import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';

import {
    faAngleDown,
    faAngleUp,
    faCircleInfo,
    faSpinnerThird,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Combobox, ComboboxOption } from '../design-system/components';

import { AuditJobRequest, AuditWorkpaperSummary, AuditWorkpaperOutputFormat, AuditWorkpaperRequirementLevel, NaicsCodeEntity, PortalTextAuditModals } from '../../Models/Api/strongbox.financialportal';
import { REQUIREMENT_TOLERABLE_MISSTATEMENT_ID, REQUIREMENT_TRIVIAL_MISSTATEMENT_ID } from '../../Utils/AuditConstants';
import { ApplicationState } from '../../Store';
import { GetAuditWorkpapers, GetMaxAuditYearsBack } from '../../Store/AuditWorkpapers';
import { GetIndustryNaicsCodes } from '../../Store/Industries';
import { GetPortalText } from '../../Store/PortalAssets';
import {
    GetRunAuditWorkpapersModalActive,
    GetRunAuditWorkpapersSubmissionId,
    GetRunAuditWorkpapersWorkspaceId,
    GetRunAuditWorkpapersFinancialRecordId,
    actionCreators as RunAuditWorkpapersActions,
} from '../../Store/RunAuditWorkpapers';

// Maps a requirement's level to the icon shown beside it. Only 'Informational' exists today (info icon);
// this switch is where additional levels (e.g. warning) would map to their own icons.
const requirementLevelIcon = (level: AuditWorkpaperRequirementLevel) => {
    switch (level) {
        case 'Informational':
        default:
            return faCircleInfo;
    }
};

type InjectedReduxState = {
    modalActive: boolean;
    workspaceId: string;
    submissionId: string;
    financialRecordId: string;
    // The set of available audit workpapers retrieved into the auditWorkpapers slice.
    workpapers: AuditWorkpaperSummary[];
    // The modal's text, retrieved into the portalAssets slice when the Run Audit Workpapers button is
    // pressed. Undefined until it arrives, which drives the loading spinner.
    auditModalsText: PortalTextAuditModals | undefined;
    // The full list of basic NAICS codes retrieved into the industries slice; backs the NAICS selector.
    naicsCodes: NaicsCodeEntity[];
    // How many years back the audit year may be selected; bounds the audit-year date picker.
    maxAuditYearsBack: number;
};

type InjectedActionCreators = typeof RunAuditWorkpapersActions;

type RunAuditWorkpapersModalProps = {
};

type Props = InjectedReduxState & InjectedActionCreators & RunAuditWorkpapersModalProps;

const RunAuditWorkpapersModalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        modalActive,
        workpapers,
        auditModalsText,
        naicsCodes,
        maxAuditYearsBack,
        HideRunAuditWorkpapersModal,
    } = props;
    // props.workspaceId and props.submissionId (from the RunAuditWorkpapers slice) identify the
    // submission the button was clicked on; they are consumed when the run action is implemented.

    const [selectedWorkpapers, setSelectedWorkpapers] = React.useState<string[]>([]);
    // Workpapers with workbooks start collapsed; this tracks the ones the user has expanded.
    const [expandedWorkpapers, setExpandedWorkpapers] = React.useState<string[]>([]);
    // Selected workbooks per workpaper, keyed by workpaper id.
    const [selectedWorkbooks, setSelectedWorkbooks] = React.useState<{ [workpaperId: string]: string[] }>({});
    // Selected output format per workpaper, keyed by workpaper id.
    const [selectedOutputFormats, setSelectedOutputFormats] = React.useState<{ [workpaperId: string]: AuditWorkpaperOutputFormat }>({});
    // Run parameters collected at the bottom of the modal. auditYear holds the native date input's
    // yyyy-MM-dd string; tolerable/trivial misstatement are dollar amounts; significantChangeThreshold
    // is a percentage (0-100); naicsCode is the selected code (dropdown population TBD). All are kept
    // as strings so the fields can be cleared.
    const [auditYear, setAuditYear] = React.useState<string>('');
    const [tolerableMisstatement, setTolerableMisstatement] = React.useState<string>('');
    const [trivialMisstatement, setTrivialMisstatement] = React.useState<string>('');
    const [significantChangeThreshold, setSignificantChangeThreshold] = React.useState<string>('');
    const [naicsCode, setNaicsCode] = React.useState<string>('');
    // Set true once Confirm is pressed; drives display of the required-field errors. Once shown, the
    // errors clear field-by-field as the user supplies the missing values.
    const [validationAttempted, setValidationAttempted] = React.useState<boolean>(false);

    // NAICS combobox options: value is the numeric code (what we keep in state / send), label is the
    // "code - description" the user searches against. Declared here (before the early return below) so
    // hook order stays stable.
    const naicsCodeOptions: ComboboxOption[] = React.useMemo(
        () => naicsCodes
            .filter(code => code.NaicsCode !== undefined && code.NaicsCode !== null)
            .map(code => ({
                value: String(code.NaicsCode),
                label: `${code.NaicsCode} - ${code.Description ?? ''}`,
            })),
        [naicsCodes]);

    const resetState = useCallback((): void => {
        setSelectedWorkpapers([]);
        setExpandedWorkpapers([]);
        setSelectedWorkbooks({});
        setSelectedOutputFormats({});
        setAuditYear('');
        setTolerableMisstatement('');
        setTrivialMisstatement('');
        setSignificantChangeThreshold('');
        setNaicsCode('');
        setValidationAttempted(false);
        HideRunAuditWorkpapersModal();
    }, [HideRunAuditWorkpapersModal]);

    React.useEffect(() => {
        if (!modalActive) {
            resetState();
        }
    }, [modalActive, resetState]);

    // Default each workpaper's output format to the first option in its list (its options are always
    // real formats, never NotApplicable). Only fills in workpapers the user hasn't already chosen for,
    // and runs on open so it re-applies after resetState clears the map on close.
    React.useEffect(() => {
        if (!modalActive) {
            return;
        }
        setSelectedOutputFormats(current => {
            let changed = false;
            const next = { ...current };
            workpapers.forEach(workpaper => {
                if (!!workpaper.outputFormats && workpaper.outputFormats.length > 0 && next[workpaper.workpaperId] === undefined) {
                    next[workpaper.workpaperId] = workpaper.outputFormats[0].format;
                    changed = true;
                }
            });
            return changed ? next : current;
        });
    }, [modalActive, workpapers]);

    const onClose = (): void => {
        resetState();
    }

    // Block keys that would introduce a decimal or other non-integer input (the fields are whole numbers
    // only). Guards typing; onChange still strips anything pasted.
    const blockNonIntegerKeys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
            e.preventDefault();
        }
    }

    // Dollar amounts — non-negative whole numbers only. Strip any non-digit (handles paste); empty clears.
    const onCurrencyChange = (value: string, setter: (v: string) => void): void => {
        setter(value.replace(/[^0-9]/g, ''));
    }

    const onSignificantChangeThresholdChange = (value: string): void => {
        const digits = value.replace(/[^0-9]/g, '');
        if (digits === '') {
            setSignificantChangeThreshold('');
            return;
        }
        // Percentage — whole number clamped to the 0-100 range.
        setSignificantChangeThreshold(String(Math.min(100, parseInt(digits, 10))));
    }

    // Custom stepper for the percentage field (it uses a text input so the "%" can sit inline, so it has
    // no native number spinner). Steps by one, clamped to 0-100.
    const stepSignificantChangeThreshold = (delta: number): void => {
        const current = significantChangeThreshold === '' ? 0 : parseInt(significantChangeThreshold, 10);
        setSignificantChangeThreshold(String(Math.min(100, Math.max(0, current + delta))));
    }

    const toggleWorkpaper = (id: string): void => {
        setSelectedWorkpapers(current =>
            current.indexOf(id) === -1
                ? current.concat(id)
                : current.filter(existing => existing !== id)
        );
    }

    const toggleExpanded = (id: string): void => {
        setExpandedWorkpapers(current =>
            current.indexOf(id) === -1
                ? current.concat(id)
                : current.filter(existing => existing !== id)
        );
    }

    const selectOutputFormat = (workpaperId: string, format: AuditWorkpaperOutputFormat): void => {
        setSelectedOutputFormats(current => ({ ...current, [workpaperId]: format }));
    }

    const toggleWorkbook = (workpaperId: string, workbookId: string): void => {
        setSelectedWorkbooks(current => {
            const existing = current[workpaperId] || [];
            const next = existing.indexOf(workbookId) === -1
                ? existing.concat(workbookId)
                : existing.filter(id => id !== workbookId);
            return { ...current, [workpaperId]: next };
        });
    }

    if (!modalActive) {
        return (<></>);
    }

    // The modal opens immediately on click while its text is fetched in parallel (RetrieveText('auditModals')
    // from the submission list); show a spinner until the text arrives.
    const loading = !auditModalsText;

    // A misstatement field is required whenever at least one currently-selected workpaper carries the
    // matching requirement id; deselecting all such workpapers clears the requirement again.
    const anySelectedWorkpaperRequires = (requirementId: string): boolean =>
        workpapers.some(workpaper =>
            selectedWorkpapers.indexOf(workpaper.workpaperId) !== -1 &&
            !!workpaper.requirements &&
            workpaper.requirements.some(requirement => requirement.id === requirementId)
        );
    const tolerableMisstatementRequired = anySelectedWorkpaperRequires(REQUIREMENT_TOLERABLE_MISSTATEMENT_ID);
    const trivialMisstatementRequired = anySelectedWorkpaperRequires(REQUIREMENT_TRIVIAL_MISSTATEMENT_ID);

    // Required-field validation. Computed each render so an error clears as soon as the user fills the
    // field, but only surfaced after Confirm has been pressed. Tolerable/trivial are only required when a
    // selected workpaper demands them.
    const workpapersError = validationAttempted && selectedWorkpapers.length === 0;
    const auditYearError = validationAttempted && auditYear.trim() === '';
    const significantChangeThresholdError = validationAttempted && significantChangeThreshold.trim() === '';
    const naicsCodeError = validationAttempted && naicsCode.trim() === '';
    const tolerableMisstatementError = validationAttempted && tolerableMisstatementRequired && tolerableMisstatement.trim() === '';
    const trivialMisstatementError = validationAttempted && trivialMisstatementRequired && trivialMisstatement.trim() === '';

    // Cross-field check: when both misstatement amounts are entered, tolerable must exceed trivial. Two
    // empty (non-required) fields are fine. Surfaced live since it's a relationship between entered values.
    const misstatementOrderInvalid =
        tolerableMisstatement.trim() !== '' &&
        trivialMisstatement.trim() !== '' &&
        Number(tolerableMisstatement) < Number(trivialMisstatement);

    // Audit-year date bounds: no future dates (max today), and no further back than maxAuditYearsBack
    // (from the auditWorkpapers slice). Formatted as local yyyy-MM-dd for the native date input.
    const toDateInputValue = (d: Date): string => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const today = new Date();
    const maxAuditYearDate = toDateInputValue(today);
    const minAuditYearDate = toDateInputValue(
        new Date(today.getFullYear() - maxAuditYearsBack, today.getMonth(), today.getDate()));

    return (
        <Modal
            // reactstrap renders through a portal on document.body, outside the themed #root, so the
            // design-system tokens (defined under [data-theme]/.theme-data-extraction) are otherwise
            // undefined here — leaving cr-* surfaces transparent and cr-input sizing broken (e.g. the
            // Combobox chevron/menu). Carry the theme onto the modal so those tokens resolve.
            className={`flight-modal run-audit-workpapers-modal theme-data-extraction`}
            isOpen={true}
            toggle={onClose}
            backdrop={'static'}
            size={'lg'}
        >
            <ModalHeader className={'run-audit-workpapers-header'} toggle={onClose}>{auditModalsText?.header}</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className={'run-audit-workpapers-loading'}>
                        <FontAwesomeIcon icon={faSpinnerThird} spin />
                    </div>
                ) : (
                    <div className={`control-region control-region-lender run-audit-workpapers-body`}>
                        <div className={'run-audit-workpapers-columns'}>
                            <div className={'run-audit-workpapers-column run-audit-workpapers-column--workpapers'}>
                                <p className={'run-audit-workpapers-label'}>
                                    {auditModalsText?.workpapersLabel}
                                    <span className={'run-audit-workpapers-required'}>*</span>
                                </p>
                                {workpapersError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.workpapersRequiredError}</p>}
                                {
                                    workpapers.map(workpaper => {
                                        const inputId = `run-audit-workpaper-${workpaper.workpaperId}`;
                                        const checked = selectedWorkpapers.indexOf(workpaper.workpaperId) !== -1;
                                        const hasWorkbooks = !!workpaper.workbooks && workpaper.workbooks.length > 0;
                                        const hasOutputFormats = !!workpaper.outputFormats && workpaper.outputFormats.length > 0;
                                        // A workpaper is expandable when it has output-format options and/or a workbook
                                        // (audit area) list; the chevron reveals both together. Starts collapsed.
                                        const hasExpandableContent = hasWorkbooks || hasOutputFormats;
                                        const expanded = hasExpandableContent && expandedWorkpapers.indexOf(workpaper.workpaperId) !== -1;
                                        const selectedForWorkpaper = selectedWorkbooks[workpaper.workpaperId] || [];
                                        const selectedOutputFormat = selectedOutputFormats[workpaper.workpaperId];
                                        // A selected workpaper that offers workbooks must have at least one chosen.
                                        const workbooksError = validationAttempted && checked && hasWorkbooks && selectedForWorkpaper.length === 0;

                                        return (
                                            <div
                                                key={workpaper.workpaperId}
                                                className={'run-audit-workpapers-card'}
                                            >
                                                <div className={'run-audit-workpapers-card-header'}>
                                                    <label
                                                        className={'run-audit-workpapers-card-main'}
                                                        htmlFor={inputId}
                                                    >
                                                        <Input
                                                            type={'checkbox'}
                                                            id={inputId}
                                                            checked={checked}
                                                            onChange={() => toggleWorkpaper(workpaper.workpaperId)}
                                                        />
                                                        <div className={'run-audit-workpapers-card-content'}>
                                                            <p className={'run-audit-workpapers-card-title'}>{workpaper.name}</p>
                                                            <p className={'run-audit-workpapers-card-description'}>{workpaper.description}</p>
                                                            {
                                                                // When the workpaper supplies a requirementsSummary, it stands in for the
                                                                // whole list — one line with the info icon. Otherwise fall back to listing
                                                                // each individual requirement's description.
                                                                workpaper.requirementsSummary ? (
                                                                    <div className={'run-audit-workpapers-card-requirements'}>
                                                                        <p className={'run-audit-workpapers-card-requirement'}>
                                                                            <FontAwesomeIcon
                                                                                className={'run-audit-workpapers-card-requirement-icon'}
                                                                                icon={faCircleInfo}
                                                                            />
                                                                            {workpaper.requirementsSummary}
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    !!workpaper.requirements && workpaper.requirements.length > 0 && (
                                                                        <div className={'run-audit-workpapers-card-requirements'}>
                                                                            {
                                                                                workpaper.requirements.map((requirement, index) => (
                                                                                    <p
                                                                                        key={index}
                                                                                        className={'run-audit-workpapers-card-requirement'}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            className={'run-audit-workpapers-card-requirement-icon'}
                                                                                            icon={requirementLevelIcon(requirement.level)}
                                                                                        />
                                                                                        {requirement.description}
                                                                                    </p>
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    )
                                                                )
                                                            }
                                                        </div>
                                                    </label>
                                                    {
                                                        hasExpandableContent && (
                                                            <button
                                                                type={'button'}
                                                                className={'run-audit-workpapers-card-chevron-button'}
                                                                aria-expanded={expanded}
                                                                aria-label={expanded ? 'Collapse details' : 'Expand details'}
                                                                onClick={() => toggleExpanded(workpaper.workpaperId)}
                                                            >
                                                                <FontAwesomeIcon
                                                                    className={`run-audit-workpapers-card-chevron${expanded ? ' run-audit-workpapers-card-chevron--expanded' : ''}`}
                                                                    icon={faAngleDown}
                                                                />
                                                            </button>
                                                        )
                                                    }
                                                </div>
                                                {
                                                    hasOutputFormats && expanded && (
                                                        <div className={'run-audit-workpapers-output-formats'}>
                                                            <p className={'run-audit-workpapers-output-formats-label'}>{auditModalsText?.outputFormatLabel}</p>
                                                            <div className={'run-audit-workpapers-output-format-options'}>
                                                                {
                                                                    workpaper.outputFormats.map(outputFormat => {
                                                                        const outputFormatInputId = `run-audit-output-format-${workpaper.workpaperId}-${outputFormat.format}`;

                                                                        return (
                                                                            <label
                                                                                key={outputFormat.format}
                                                                                className={'run-audit-workpapers-output-format'}
                                                                                htmlFor={outputFormatInputId}
                                                                            >
                                                                                <Input
                                                                                    type={'radio'}
                                                                                    id={outputFormatInputId}
                                                                                    name={`run-audit-output-format-${workpaper.workpaperId}`}
                                                                                    checked={selectedOutputFormat === outputFormat.format}
                                                                                    onChange={() => selectOutputFormat(workpaper.workpaperId, outputFormat.format)}
                                                                                />
                                                                                <span className={'run-audit-workpapers-output-format-description'}>{outputFormat.description}</span>
                                                                            </label>
                                                                        );
                                                                    })
                                                                }
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                                {
                                                    hasOutputFormats && hasWorkbooks && expanded && (
                                                        <div className={'run-audit-workpapers-output-formats-separator'} />
                                                    )
                                                }
                                                {
                                                    // Right below the output-formats divider and above the workbook list.
                                                    workbooksError && expanded && (
                                                        <p className={'run-audit-workpapers-field-error run-audit-workpapers-workbooks-error'}>{auditModalsText?.workbooksRequiredError}</p>
                                                    )
                                                }
                                                {
                                                    hasWorkbooks && expanded && (
                                                        <div className={'run-audit-workpapers-workbooks'}>
                                                            {
                                                                workpaper.workbooks!.map(workbook => {
                                                                    const workbookInputId = `run-audit-workbook-${workpaper.workpaperId}-${workbook.id}`;
                                                                    const workbookChecked = selectedForWorkpaper.indexOf(workbook.id) !== -1;

                                                                    return (
                                                                        <label
                                                                            key={workbook.id}
                                                                            className={'run-audit-workpapers-workbook'}
                                                                            htmlFor={workbookInputId}
                                                                        >
                                                                            <Input
                                                                                type={'checkbox'}
                                                                                id={workbookInputId}
                                                                                checked={workbookChecked}
                                                                                onChange={() => toggleWorkbook(workpaper.workpaperId, workbook.id)}
                                                                            />
                                                                            <span className={'run-audit-workpapers-workbook-description'}>{workbook.description}</span>
                                                                        </label>
                                                                    );
                                                                })
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <div className={'run-audit-workpapers-column run-audit-workpapers-column--fields'}>
                                <div className={'run-audit-workpapers-fields'}>
                                    <div className={'run-audit-workpapers-field'}>
                                        <label className={'run-audit-workpapers-field-label'} htmlFor={'run-audit-audit-year'}>
                                            {auditModalsText?.auditYearLabel}
                                            <sup className={'run-audit-workpapers-required'}>*</sup>
                                        </label>
                                        <div className={'cr-input run-audit-workpapers-field-control'}>
                                            <input
                                                type={'date'}
                                                id={'run-audit-audit-year'}
                                                className={'cr-input__control page-cursor-pointer'}
                                                min={minAuditYearDate}
                                                max={maxAuditYearDate}
                                                value={auditYear}
                                                onChange={(e) => setAuditYear(e.currentTarget.value)}
                                            />
                                        </div>
                                        {auditYearError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.auditYearRequiredError}</p>}
                                    </div>
                                    <div className={'run-audit-workpapers-field'}>
                                        <label className={'run-audit-workpapers-field-label'} htmlFor={'run-audit-tolerable-misstatement'}>
                                            {auditModalsText?.tolerableMisstatementLabel}
                                            {tolerableMisstatementRequired && <sup className={'run-audit-workpapers-required'}>*</sup>}
                                        </label>
                                        <div className={'run-audit-workpapers-affix-input run-audit-workpapers-field-control'}>
                                            <span className={'run-audit-workpapers-affix'}>$</span>
                                            <input
                                                type={'number'}
                                                id={'run-audit-tolerable-misstatement'}
                                                className={'run-audit-workpapers-affix-control'}
                                                min={0}
                                                step={1}
                                                value={tolerableMisstatement}
                                                onKeyDown={blockNonIntegerKeys}
                                                onChange={(e) => onCurrencyChange(e.currentTarget.value, setTolerableMisstatement)}
                                            />
                                        </div>
                                        <p className={'run-audit-workpapers-field-description'}>{auditModalsText?.tolerableMisstatementDescription}</p>
                                        {tolerableMisstatementError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.tolerableMisstatementRequiredError}</p>}
                                        {misstatementOrderInvalid && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.tolerableMisstatementOrderError}</p>}
                                    </div>
                                    <div className={'run-audit-workpapers-field'}>
                                        <label className={'run-audit-workpapers-field-label'} htmlFor={'run-audit-trivial-misstatement'}>
                                            {auditModalsText?.trivialMisstatementLabel}
                                            {trivialMisstatementRequired && <sup className={'run-audit-workpapers-required'}>*</sup>}
                                        </label>
                                        <div className={'run-audit-workpapers-affix-input run-audit-workpapers-field-control'}>
                                            <span className={'run-audit-workpapers-affix'}>$</span>
                                            <input
                                                type={'number'}
                                                id={'run-audit-trivial-misstatement'}
                                                className={'run-audit-workpapers-affix-control'}
                                                min={0}
                                                step={1}
                                                value={trivialMisstatement}
                                                onKeyDown={blockNonIntegerKeys}
                                                onChange={(e) => onCurrencyChange(e.currentTarget.value, setTrivialMisstatement)}
                                            />
                                        </div>
                                        <p className={'run-audit-workpapers-field-description'}>{auditModalsText?.trivialMisstatementDescription}</p>
                                        {trivialMisstatementError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.trivialMisstatementRequiredError}</p>}
                                        {misstatementOrderInvalid && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.trivialMisstatementOrderError}</p>}
                                    </div>
                                    <div className={'run-audit-workpapers-field'}>
                                        <label className={'run-audit-workpapers-field-label'} htmlFor={'run-audit-significant-change-threshold'}>
                                            {auditModalsText?.significantChangeThresholdLabel}
                                            <sup className={'run-audit-workpapers-required'}>*</sup>
                                        </label>
                                        <div className={'run-audit-workpapers-affix-input run-audit-workpapers-affix-input--suffix run-audit-workpapers-field-control'}>
                                            {/* A text input (not number) so the "%" sits inline right after the digits,
                                            reading as "20%". The input auto-sizes to its content via the sizer's
                                            data-value mirror; the custom stepper then follows on the right. */}
                                            <span className={'run-audit-workpapers-affix-sizer'} data-value={significantChangeThreshold}>
                                                <input
                                                    type={'text'}
                                                    inputMode={'numeric'}
                                                    size={1}
                                                    id={'run-audit-significant-change-threshold'}
                                                    className={'run-audit-workpapers-affix-control'}
                                                    value={significantChangeThreshold}
                                                    onKeyDown={blockNonIntegerKeys}
                                                    onChange={(e) => onSignificantChangeThresholdChange(e.currentTarget.value)}
                                                />
                                            </span>
                                            <span className={'run-audit-workpapers-affix'}>%</span>
                                            <span className={'run-audit-workpapers-stepper'}>
                                                <button
                                                    type={'button'}
                                                    className={'run-audit-workpapers-stepper-button'}
                                                    aria-label={'Increase significant change threshold'}
                                                    onClick={() => stepSignificantChangeThreshold(1)}
                                                >
                                                    <FontAwesomeIcon icon={faAngleUp} />
                                                </button>
                                                <button
                                                    type={'button'}
                                                    className={'run-audit-workpapers-stepper-button'}
                                                    aria-label={'Decrease significant change threshold'}
                                                    onClick={() => stepSignificantChangeThreshold(-1)}
                                                >
                                                    <FontAwesomeIcon icon={faAngleDown} />
                                                </button>
                                            </span>
                                        </div>
                                        <p className={'run-audit-workpapers-field-description'}>{auditModalsText?.significantChangeThresholdDescription}</p>
                                        {significantChangeThresholdError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.significantChangeThresholdRequiredError}</p>}
                                    </div>
                                    <div className={'run-audit-workpapers-field'}>
                                        <label className={'run-audit-workpapers-field-label'} htmlFor={'run-audit-naics-code'}>
                                            {auditModalsText?.naicsCodeLabel}
                                            <sup className={'run-audit-workpapers-required'}>*</sup>
                                        </label>
                                        {/* Searchable combobox over the basic NAICS list loaded into the industries
                                        slice. Filters by whatever the user types — a partial number or partial
                                        description — against each option's "code - description" label. */}
                                        <Combobox
                                            id={'run-audit-naics-code'}
                                            className={'run-audit-workpapers-field-control'}
                                            options={naicsCodeOptions}
                                            value={naicsCode}
                                            onChange={setNaicsCode}
                                            placeholder={auditModalsText?.naicsCodePlaceholder}
                                            error={naicsCodeError}
                                        />
                                        <p className={'run-audit-workpapers-field-description'}>{auditModalsText?.naicsCodeDescription}</p>
                                        {naicsCodeError && <p className={'run-audit-workpapers-field-error'}>{auditModalsText?.naicsCodeRequiredError}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>
            {!loading && (
                <ModalFooter className={'run-audit-workpapers-footer'}>
                    {/* Actions come first in source so the disclaimer paragraph flows around them: text wraps in
                    the space to their left, then extends full width below (see float in the SCSS). */}
                    <div className={'run-audit-workpapers-footer-actions'}>
                        <Button
                            color={'link'}
                            onClick={onClose}
                        >
                            {auditModalsText?.cancel}
                        </Button>
                        <Button
                            color={'primary'}
                            onClick={() => {
                                // Confirm is always enabled: the design directs the user to any unfilled required
                                // fields on click rather than disabling the button. Flip validationAttempted so the
                                // required-field errors render, then bail if any required value is missing.
                                setValidationAttempted(true);

                                // Selected workpapers that offer workbooks but have none chosen. Expand them so
                                // the per-workpaper "select one or more workbooks" error is visible.
                                const workpapersMissingWorkbooks = selectedWorkpapers.filter(workpaperId => {
                                    const workpaper = workpapers.find(w => w.workpaperId === workpaperId);
                                    return !!workpaper &&
                                        !!workpaper.workbooks && workpaper.workbooks.length > 0 &&
                                        (selectedWorkbooks[workpaperId] ?? []).length === 0;
                                });
                                if (workpapersMissingWorkbooks.length > 0) {
                                    setExpandedWorkpapers(current =>
                                        current.concat(workpapersMissingWorkbooks.filter(id => current.indexOf(id) === -1)));
                                }

                                const hasMissingRequiredFields =
                                    selectedWorkpapers.length === 0 ||
                                    auditYear.trim() === '' ||
                                    significantChangeThreshold.trim() === '' ||
                                    naicsCode.trim() === '' ||
                                    (tolerableMisstatementRequired && tolerableMisstatement.trim() === '') ||
                                    (trivialMisstatementRequired && trivialMisstatement.trim() === '');

                                if (hasMissingRequiredFields || misstatementOrderInvalid || workpapersMissingWorkbooks.length > 0) {
                                    return;
                                }

                                // Build the job request from the collected inputs. One definition per selected
                                // workpaper, carrying its chosen output format and selected workbooks (areas).
                                const request: AuditJobRequest = {
                                    workpapers: selectedWorkpapers.map(workpaperId => ({
                                        workpaperId,
                                        outputFormat: selectedOutputFormats[workpaperId] ?? 'NotApplicable',
                                        selectedWorkpapers: selectedWorkbooks[workpaperId] ?? [],
                                    })),
                                    auditYear: new Date(auditYear).toISOString(),
                                    // Optional amounts default to 0 when left blank; the required fields are
                                    // guaranteed non-empty by the validation above.
                                    tolerableMisstatement: tolerableMisstatement === '' ? 0 : parseInt(tolerableMisstatement, 10),
                                    trivialMisstatement: trivialMisstatement === '' ? 0 : parseInt(trivialMisstatement, 10),
                                    significantChangeThreshold: parseInt(significantChangeThreshold, 10),
                                    naicsCode: parseInt(naicsCode, 10),
                                    // workspaceId is the entity id the modal was launched from.
                                    entityId: props.workspaceId,
                                    financialRecordId: props.financialRecordId,
                                };

                                // Start the audit job; the thunk closes the modal on success.
                                props.StartAuditJob(request);
                            }}
                        >
                            {auditModalsText?.confirm}
                        </Button>
                    </div>
                    <p className={'run-audit-workpapers-disclaimer'}>
                        {auditModalsText?.aiNoticeDescription}
                        <a
                            href={auditModalsText?.privacyPolicyUrl}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            {auditModalsText?.privacyPolicy}
                        </a>
                    </p>
                </ModalFooter>
            )}
        </Modal>
    );
}

export const RunAuditWorkpapersModal = connect<InjectedReduxState, InjectedActionCreators, RunAuditWorkpapersModalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            modalActive: GetRunAuditWorkpapersModalActive(appState),
            workspaceId: GetRunAuditWorkpapersWorkspaceId(appState),
            submissionId: GetRunAuditWorkpapersSubmissionId(appState),
            financialRecordId: GetRunAuditWorkpapersFinancialRecordId(appState),
            workpapers: GetAuditWorkpapers(appState),
            auditModalsText: GetPortalText<PortalTextAuditModals>(appState, 'auditModals'),
            naicsCodes: GetIndustryNaicsCodes(appState),
            maxAuditYearsBack: GetMaxAuditYearsBack(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...RunAuditWorkpapersActions,
        },
        dispatch
    )
)(RunAuditWorkpapersModalComponent);
